import * as React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useGetUserVacationRequestsQuery } from '../../../../features/vacations/vacationsApiSlice'
import useOwnUser from 'hooks/useOwnUser'
import { IVacationRequest } from '@/pkg/requests'
import { useEffect } from 'react'
import {
  FaCalendarAlt,
  FaClock,
  FaExclamationCircle,
  FaPlus,
  FaHistory,
  FaChartBar,
} from 'react-icons/fa'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'

// Modern styled components
const PageWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 2rem;
  background-color: #f8f9fd;
  min-height: 100vh;
`

const ContentContainer = styled.div`
  display: flex;
  width: 100%;
  max-width: 1200px;
  gap: 2rem;

  @media (max-width: 992px) {
    flex-direction: column;
  }
`

const MainCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  padding: 2rem;
  transition: transform 0.2s ease;
  flex: 2;

  &:hover {
    transform: translateY(-5px);
  }
`

const SidePanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const ActionCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  padding: 1.5rem;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 14px 30px rgba(0, 0, 0, 0.12);
  }
`

const Header = styled.div`
  margin-bottom: 2rem;
  border-bottom: 1px solid #f0f0f7;
  padding-bottom: 1.5rem;
`

const Title = styled.h1`
  font-size: 1.8rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
`

const Subtitle = styled.p`
  color: #718096;
  margin-top: 0.5rem;
  font-size: 1rem;
`

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;
`

const StatCard = styled.div<{ color: string }>`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border-left: 4px solid ${(props) => props.color};
  display: flex;
  flex-direction: column;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`

const StatIcon = styled.div<{ color: string }>`
  background-color: ${(props) => `${props.color}15`};
  color: ${(props) => props.color};
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
`

const StatTitle = styled.span`
  font-weight: 600;
  color: #4a5568;
  font-size: 1rem;
`

const StatValue = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: #2d3748;
`

const StatMeta = styled.div`
  font-size: 0.875rem;
  color: #718096;
  margin-top: 0.5rem;
`

const CardTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;

  svg {
    margin-right: 0.5rem;
    color: #4299e1;
  }
`

const ActionButton = styled.button`
  background: #4299e1;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 1rem;

  &:hover {
    background: #3182ce;
    transform: translateY(-2px);
  }

  svg {
    margin-right: 0.5rem;
  }
`

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`

const InfoItem = styled.li`
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f7;
  color: #4a5568;
  font-size: 0.95rem;

  &:last-child {
    border-bottom: none;
  }

  strong {
    color: #2d3748;
  }
`

const Market: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { companyProfiles, selectedCompanyID, userUID } = useOwnUser()
  const profile = companyProfiles?.find((p) => p.id === selectedCompanyID)
  const currentYear = new Date().getFullYear()

  const { data: requests, isSuccess } = useGetUserVacationRequestsQuery(
    { userID: Number(userUID), companyID: selectedCompanyID },
    { skip: !userUID || !selectedCompanyID },
  )

  interface YearSummary {
    available: number
    used: number
    pending: number
    lastRequestsDate: string
    thisYearsRequestCount: number
    thisYearsApprovedRequestCount: number
  }

  const [summary, setSummary] = React.useState<YearSummary>({
    available: 0,
    used: 0,
    pending: 0,
    lastRequestsDate: '',
    thisYearsRequestCount: 0,
    thisYearsApprovedRequestCount: 0,
  })

  const parseLeaveRequestsForSummary = (requests: IVacationRequest[]): YearSummary => {
    const today = new Date()
    const currentYear = today.getFullYear()

    const reqThisYear = requests.filter((request) => {
      const requestYear = new Date(request.starting_date).getFullYear()
      return requestYear === currentYear
    })

    return {
      available: profile?.available_vacation_days ?? 0,
      used: reqThisYear?.reduce((acc, req) => {
        if (req.status === 'approved') {
          return acc + req.hours / 8
        } else {
          return acc
        }
      }, 0),
      pending: reqThisYear?.reduce((acc, req) => {
        if (req.status === 'pending') {
          return acc + req.hours / 8
        } else {
          return acc
        }
      }, 0),
      thisYearsApprovedRequestCount: reqThisYear?.reduce((acc, req) => {
        if (req.status === 'approved') {
          return acc + 1
        } else {
          return acc
        }
      }, 0),
      thisYearsRequestCount: reqThisYear.length,
      lastRequestsDate: reqThisYear.length !== 0 ? reqThisYear[0].created_at : '',
    }
  }

  useEffect(() => {
    if (requests) {
      setSummary(parseLeaveRequestsForSummary(requests))
    }
  }, [requests])

  return (
    <PageWrapper>
      <ContentContainer>
        <MainCard>
          <Header>
            <Title>{t('JobMarket:Header.header')}</Title>
            <Subtitle>{t('JobMarket:Header.subTitle', { year: currentYear })}</Subtitle>
          </Header>

          <StatsContainer>
            <StatCard color='#10B981'>
              <StatHeader>
                <StatIcon color='#10B981'>
                  <FaCalendarAlt size={18} />
                </StatIcon>
                <StatTitle>{t('JobMarket:Stats.remaining')}</StatTitle>
              </StatHeader>
              <StatValue>{t('JobMarket:Stats.days', { count: summary.available })}</StatValue>
              <StatMeta>{t('JobMarket:Stats.hours', { count: summary.available * 8 })}</StatMeta>
            </StatCard>

            <StatCard color='#F59E0B'>
              <StatHeader>
                <StatIcon color='#F59E0B'>
                  <FaClock size={18} />
                </StatIcon>
                <StatTitle>{t('JobMarket:Stats.used')}</StatTitle>
              </StatHeader>
              <StatValue>{t('JobMarket:Stats.days', { count: summary.used })}</StatValue>
              <StatMeta>{t('JobMarket:Stats.hours', { count: summary.used * 8 })}</StatMeta>
            </StatCard>

            <StatCard color='#3B82F6'>
              <StatHeader>
                <StatIcon color='#3B82F6'>
                  <FaExclamationCircle size={18} />
                </StatIcon>
                <StatTitle>{t('JobMarket:Stats.pending')}</StatTitle>
              </StatHeader>
              <StatValue>{t('JobMarket:Stats.days', { count: summary.pending })}</StatValue>
              <StatMeta>{t('JobMarket:Stats.hours', { count: summary.pending * 8 })}</StatMeta>
            </StatCard>
          </StatsContainer>
        </MainCard>

        <SidePanel>
          <ActionCard>
            <CardTitle>
              <FaPlus size={16} /> {t('JobMarket:Cards.newRequestTitle')}
            </CardTitle>
            <p>{t('JobMarket:Cards.newRequestDesc')}</p>
            <ActionButton onClick={() => navigate('/vacation')}>
              <FaPlus size={14} /> {t('JobMarket:Buttons.newRequest')}
            </ActionButton>
          </ActionCard>

          <ActionCard>
            <CardTitle>
              <FaHistory size={16} /> {t('JobMarket:Cards.historyTitle')}
            </CardTitle>
            <InfoList>
              <InfoItem>
                <strong>{t('JobMarket:Cards.lastRequest')}:</strong>{' '}
                {summary.lastRequestsDate ?
                  dayjs(summary.lastRequestsDate).format('DD-MM-YYYY')
                : '-'}
              </InfoItem>
              <InfoItem>
                <strong>{t('JobMarket:Cards.requestsThisYear')}:</strong>{' '}
                {summary.thisYearsRequestCount}
              </InfoItem>
              <InfoItem>
                <strong>{t('JobMarket:Cards.acceptedRequests')}:</strong>{' '}
                {summary.thisYearsApprovedRequestCount}
              </InfoItem>
            </InfoList>
            <ActionButton onClick={() => navigate('/vacation')}>
              <FaHistory size={14} /> {t('JobMarket:Buttons.viewAll')}
            </ActionButton>
          </ActionCard>
        </SidePanel>
      </ContentContainer>
    </PageWrapper>
  )
}

export default Market
